// src/components/LoginPage.jsx
import React, { useState, useEffect } from "react";
import { Auth, Hub } from "aws-amplify";
import { Box, Button, TextField, Typography, Paper, Container } from "../ui";
import { brand, companiesSentence, microsoftSignIn } from "../config/brand";

/**
 * Every organization's logo, side by side: the portal is all of theirs, so the
 * sign-in page shows no one company's alone. Each is capped to the same box
 * so a wide wordmark does not outweigh a tall emblem. With one organization it
 * is the shell's logo, as it always was.
 */
function SignInLogos() {
  const organizations = brand.organizations ?? [];
  const logos =
    organizations.length > 1
      ? organizations.map((o) => ({ src: o.logo, alt: o.logoAlt }))
      : [{ src: brand.logo, alt: brand.logoAlt }];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: 4,
        mb: 3,
      }}
    >
      {logos.map((logo) => (
        <Box
          key={logo.alt}
          component="img"
          src={logo.src}
          alt={logo.alt}
          sx={{ maxHeight: 60, maxWidth: 160 }}
        />
      ))}
    </Box>
  );
}

/**
 * The sentence to show for a failed sign-in.
 *
 * A refusal from the pre-sign-up trigger comes back through the OAuth redirect
 * as "PreSignUp failed with error <message>.", sometimes with + for spaces.
 * The trigger's own message is the part worth showing.
 */
export function signInErrorMessage(raw) {
  const text = String(raw || "").replace(/\+/g, " ");
  const refused = text.match(/PreSignUp failed with error (.*?)\.?$/);
  if (refused) return refused[1].replace(/\.$/, "") + ".";
  return "Sign-in failed: " + (text || "Unknown error") + ". Please try again.";
}

/** Microsoft's four-square logo, as its sign-in button guidelines ask. */
const MicrosoftLogo = () => (
  <svg width="21" height="21" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="0" y="0" width="10" height="10" fill="#f25022" />
    <rect x="11" y="0" width="10" height="10" fill="#7fba00" />
    <rect x="0" y="11" width="10" height="10" fill="#00a4ef" />
    <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
  </svg>
);

export default function LoginPage({ onAuthenticated }) {
  // Staff sign in with Microsoft; the email form is for the few accounts
  // outside their domain, so it waits behind a link when Microsoft is offered.
  const [showEmailForm, setShowEmailForm] = useState(!microsoftSignIn);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Challenge states
  const [challengeUser, setChallengeUser] = useState(null);
  const [challengeType, setChallengeType] = useState(null); // "NEW_PASSWORD"
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  // Forgot password states
  const [forgotStep, setForgotStep] = useState(null); // "REQUEST" | "CONFIRM"
  const [resetCode, setResetCode] = useState("");

  useEffect(() => {
    // Amplify may still be exchanging tokens when this mounts, so check for an
    // existing session and also listen for the Hub event.
    checkExistingSession();

    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signIn") {
        onAuthenticated();
        return;
      }
      if (payload.event === "signIn_failure") {
        setError(signInErrorMessage(payload.data?.message || payload.data));
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const checkExistingSession = async () => {
    try {
      await Auth.currentAuthenticatedUser();
      onAuthenticated();
    } catch {
      // Not signed in - the login form below is the right thing to show.
    }
  };

  // --- Microsoft sign-in: leaves the page, and returns through the Hub ---
  const handleMicrosoftSignIn = async () => {
    setLoading(true);
    setError("");
    try {
      await Auth.federatedSignIn({ customProvider: microsoftSignIn });
    } catch (err) {
      setError(signInErrorMessage(err?.message));
      setLoading(false);
    }
  };

  // --- Email/Password sign-in ---
  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter both email and password.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const user = await Auth.signIn(email, password);

      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        setChallengeUser(user);
        setChallengeType("NEW_PASSWORD");
        setLoading(false);
        return;
      }

      // No challenge — sign-in complete
      onAuthenticated();
    } catch (err) {
      setError(err.message || "Sign-in failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  // --- New password submission ---
  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await Auth.completeNewPassword(challengeUser, newPassword);

      onAuthenticated();
    } catch (err) {
      setError(err.message || "Failed to set new password.");
    } finally {
      setLoading(false);
    }
  };

  // --- Forgot password: request code ---
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await Auth.forgotPassword(email);
      setForgotStep("CONFIRM");
    } catch (err) {
      setError(err.message || "Failed to send reset code.");
    } finally {
      setLoading(false);
    }
  };

  // --- Forgot password: submit new password ---
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetCode) {
      setError("Please enter the verification code.");
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!/\d/.test(newPassword) || !/[^a-zA-Z0-9]/.test(newPassword)) {
      setError("Password must include a number and a special character.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await Auth.forgotPasswordSubmit(email, resetCode, newPassword);
      // Reset succeeded — go back to login
      setForgotStep(null);
      setResetCode("");
      setNewPassword("");
      setConfirmNewPassword("");
      setError("");
    } catch (err) {
      setError(err.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setForgotStep(null);
    setChallengeType(null);
    setChallengeUser(null);
    setError("");
    setResetCode("");
    setNewPassword("");
    setConfirmNewPassword("");
  };

  // --- Render: Forgot password - request code ---
  if (forgotStep === "REQUEST") {
    return (
      <Container maxWidth="xs">
        <Box
          sx={{
            mt: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <SignInLogos />
          <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
            <Typography variant="h5" align="center" gutterBottom>
              Reset Password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your email address and we'll send you a verification code.
            </Typography>
            <Box component="form" onSubmit={handleForgotPassword}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 2 }}
                autoComplete="email"
                autoFocus
              />
              {error && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ py: 1.2 }}
              >
                {loading ? "Sending..." : "Send Reset Code"}
              </Button>
            </Box>
            <Button
              fullWidth
              variant="text"
              onClick={handleBackToLogin}
              sx={{ mt: 1 }}
            >
              Back to Sign In
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  // --- Render: Forgot password - enter code + new password ---
  if (forgotStep === "CONFIRM") {
    return (
      <Container maxWidth="xs">
        <Box
          sx={{
            mt: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <SignInLogos />
          <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
            <Typography variant="h5" align="center" gutterBottom>
              Enter Verification Code
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A verification code has been sent to your email. Enter it below
              along with your new password.
            </Typography>
            <Box component="form" onSubmit={handleResetPassword}>
              <TextField
                fullWidth
                label="Verification Code"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2 }}
                autoComplete="new-password"
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                sx={{ mb: 2 }}
                autoComplete="new-password"
              />
              {error && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ py: 1.2 }}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </Box>
            <Button
              fullWidth
              variant="text"
              onClick={handleBackToLogin}
              sx={{ mt: 1 }}
            >
              Back to Sign In
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  // --- Render: New password challenge ---
  if (challengeType === "NEW_PASSWORD") {
    return (
      <Container maxWidth="xs">
        <Box
          sx={{
            mt: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <SignInLogos />

          <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
            <Typography variant="h5" align="center" gutterBottom>
              Set New Password
            </Typography>
            <Typography variant="body2" align="center" sx={{ mb: 3 }}>
              You must set a new password before continuing.
            </Typography>

            <Box component="form" onSubmit={handleNewPasswordSubmit}>
              <TextField
                fullWidth
                type="password"
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2 }}
                autoFocus
              />
              <TextField
                fullWidth
                type="password"
                label="Confirm New Password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Typography variant="caption" sx={{ display: "block", mb: 2 }}>
                Password must be at least 8 characters, include a number and a
                special character.
              </Typography>

              {error && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}

              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? "Setting Password..." : "Set Password & Sign In"}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // --- Render: Main login form ---
  return (
    <Container maxWidth="xs">
      <Box
        sx={{
          mt: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <SignInLogos />

        <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
          <Typography variant="h5" align="center" gutterBottom>
            {brand.name}
          </Typography>

          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Warehouse inventory for {companiesSentence()}
          </Typography>

          {!showEmailForm && (
            <>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleMicrosoftSignIn}
                disabled={loading}
                startIcon={<MicrosoftLogo />}
                sx={{
                  py: 1.6,
                  fontSize: "1.05rem",
                  textTransform: "none",
                  color: "text.primary",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                {loading ? "Redirecting..." : "Sign in with Microsoft"}
              </Button>

              {error && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                  {error}
                </Typography>
              )}

              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setError("");
                  setShowEmailForm(true);
                }}
                sx={{
                  display: "block",
                  mx: "auto",
                  mt: 3,
                  textTransform: "none",
                  color: "text.secondary",
                }}
              >
                Sign in with email
              </Button>
            </>
          )}

          {showEmailForm && (
            <Box component="form" onSubmit={handleEmailSignIn}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 2 }}
                autoComplete="email"
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 2 }}
                autoComplete="current-password"
              />

              {error && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}

              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ py: 1.2 }}
              >
                {loading ? "Signing In..." : "Sign In"}
              </Button>
            </Box>
          )}

          {showEmailForm && (
            <Button
              fullWidth
              variant="text"
              onClick={() => {
                setError("");
                setForgotStep("REQUEST");
              }}
              sx={{ mt: 1, textTransform: "none" }}
            >
              Forgot password?
            </Button>
          )}

          {showEmailForm && microsoftSignIn && (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setError("");
                setShowEmailForm(false);
              }}
              sx={{
                display: "block",
                mx: "auto",
                mt: 1,
                textTransform: "none",
                color: "text.secondary",
              }}
            >
              Back to Sign in with Microsoft
            </Button>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
